import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, Save, Sparkles, Globe, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGS = [
  { key: 'en', label: 'English' },
  { key: 'ar', label: 'العربية', dir: 'rtl' },
  { key: 'fr', label: 'Français' },
];

const FIELDS = [
  { key: 'generic_name', label: { en: 'Generic Name', ar: 'الاسم العلمي', fr: 'Nom générique' } },
  { key: 'category', label: { en: 'Category', ar: 'الفئة', fr: 'Catégorie' } },
  { key: 'description', label: { en: 'Description', ar: 'الوصف', fr: 'Description' } },
  { key: 'indications', label: { en: 'Indications', ar: 'دواعي الاستعمال', fr: 'Indications' } },
  { key: 'dosage', label: { en: 'Dosage', ar: 'الجرعة', fr: 'Posologie' } },
  { key: 'side_effects', label: { en: 'Side Effects', ar: 'الآثار الجانبية', fr: 'Effets secondaires' } },
  { key: 'contraindications', label: { en: 'Contraindications', ar: 'موانع الاستعمال', fr: 'Contre-indications' } },
  { key: 'storage', label: { en: 'Storage', ar: 'التخزين', fr: 'Conservation' } },
];

// NutriTruth-style loading animation styles
const loadingStyles = `
  @keyframes pulse-soft {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }
  .analyzing-text {
    animation: pulse-soft 1.5s infinite;
  }
  @keyframes spin-smooth {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .spinner-smooth {
    animation: spin-smooth 1s linear infinite;
  }
`;

// Parse saved description text back into structured {en, ar, fr} object
function parseSavedDescription(text) {
  if (!text) return null;
  const langHeaders = { '[English]': 'en', '[العربية]': 'ar', '[Français]': 'fr' };
  const result = {};
  let currentLang = null;
  let currentLines = [];

  const flush = () => {
    if (currentLang && currentLines.length) {
      const data = {};
      for (const line of currentLines) {
        for (const field of FIELDS) {
          const label = field.label[currentLang] || field.label.en;
          if (line.startsWith(label + ': ')) {
            data[field.key] = line.substring(label.length + 2);
            break;
          }
        }
      }
      if (Object.keys(data).length > 0) result[currentLang] = data;
    }
  };

  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const langKey = langHeaders[trimmed];
    if (langKey) {
      flush();
      currentLang = langKey;
      currentLines = [];
    } else if (currentLang) {
      currentLines.push(trimmed);
    }
  }
  flush();

  return Object.keys(result).length > 0 ? result : null;
}

const AIMedicineInfoDialog = ({ open, onOpenChange, itemName, itemId, organizationId, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState(null);
  const [fromSaved, setFromSaved] = useState(false);
  const [activeLang, setActiveLang] = useState('en');
  const [error, setError] = useState(null);
  const [currentItemId, setCurrentItemId] = useState(null);

  // On open: check for saved description first
  useEffect(() => {
    if (open && itemId) {
      setInfo(null);
      setError(null);
      setFromSaved(false);
      setActiveLang('en');
      setCurrentItemId(itemId); // Store itemId for save operation
      setLoadingSaved(true);
      axios.get(`${API}/inventory/${itemId}/description`)
        .then(res => {
          const desc = res.data?.description;
          if (desc) {
            const parsed = parseSavedDescription(desc);
            if (parsed) {
              setInfo(parsed);
              setFromSaved(true);
              return;
            }
          }
          // No saved description — show fetch button
        })
        .catch(() => {})
        .finally(() => setLoadingSaved(false));
    }
  }, [open, itemId]);

  const fetchFromAI = async () => {
    setLoading(true);
    setError(null);
    setInfo(null);
    setFromSaved(false);
    try {
      const res = await axios.post(`${API}/inventory/ai-description`, {
        item_name: itemName,
        item_id: currentItemId || itemId,
        organization_id: organizationId,
      }, {
        timeout: 60000 // 60 second timeout
      });
      if (res.data.medicine_info) {
        setInfo(res.data.medicine_info);
      } else if (res.data.raw_text) {
        setError('Could not parse AI response. Raw: ' + res.data.raw_text.substring(0, 300));
      }
    } catch (err) {
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Request timed out. The AI service is taking too long. Please try again.');
      } else if (err.response?.status === 504) {
        setError('AI request timed out. Please try again in a moment.');
      } else {
        setError(err.response?.data?.detail || 'Failed to fetch medicine info. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const saveItemId = currentItemId || itemId;
    if (!info || !saveItemId) {
      toast.error('Cannot save: Item ID is missing');
      return;
    }
    setSaving(true);
    
    const parts = LANGS.map(lang => {
      const data = info[lang.key];
      if (!data) return '';
      return [
        `[${lang.label}]`,
        data.generic_name && `${FIELDS[0].label[lang.key]}: ${data.generic_name}`,
        data.description && `${FIELDS[2].label[lang.key]}: ${data.description}`,
        data.indications && `${FIELDS[3].label[lang.key]}: ${data.indications}`,
        data.dosage && `${FIELDS[4].label[lang.key]}: ${data.dosage}`,
        data.side_effects && `${FIELDS[5].label[lang.key]}: ${data.side_effects}`,
        data.contraindications && `${FIELDS[6].label[lang.key]}: ${data.contraindications}`,
        data.storage && `${FIELDS[7].label[lang.key]}: ${data.storage}`,
      ].filter(Boolean).join('\n');
    }).filter(Boolean);
    const description = parts.join('\n\n');

    try {
      const response = await axios.post(`${API}/inventory/${saveItemId}/save-description`, { description }, {
        timeout: 15000 // 15 second timeout for save
      });
      
      // Check if we got a successful response
      if (response.status === 200 || response.data?.message) {
        toast.success('Description saved to inventory');
        setFromSaved(true);
        
        // Call onSaved callback separately
        if (onSaved) {
          try {
            await Promise.resolve(onSaved());
          } catch (callbackErr) {
            console.warn('onSaved callback error (save was successful):', callbackErr);
          }
        }
      } else {
        throw new Error('Unexpected response');
      }
    } catch (err) {
      // Check if this is a network/timeout error but the save might have succeeded
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout') || err.message === 'Network Error') {
        // Verify if the save actually worked by checking the saved description
        try {
          const verifyRes = await axios.get(`${API}/inventory/${saveItemId}/description`);
          if (verifyRes.data?.description && verifyRes.data.description.includes('[English]')) {
            // The save actually worked!
            toast.success('Description saved to inventory');
            setFromSaved(true);
            if (onSaved) {
              try { await Promise.resolve(onSaved()); } catch {} 
            }
            return;
          }
        } catch {
          // Verification failed, show original error
        }
      }
      
      // Show error only if we're sure it failed
      const errorMsg = err.response?.data?.detail || 'Failed to save description. Please try again.';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const langData = info?.[activeLang] || {};

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setInfo(null); setError(null); setFromSaved(false); } onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto" data-testid="ai-medicine-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-400" />
            AI Medicine Info — {itemName}
          </DialogTitle>
        </DialogHeader>

        {/* Loading saved description */}
        {loadingSaved && !info && !loading && (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground text-sm">Checking saved info...</p>
          </div>
        )}

        {/* No saved data, no AI data yet — show fetch button */}
        {!info && !loading && !loadingSaved && !error && (
          <div className="flex flex-col items-center justify-center py-12 gap-5">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center">
                <Sparkles className="w-10 h-10 text-emerald-500" />
              </div>
              <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                <span className="text-white text-[10px] font-bold">AI</span>
              </div>
            </div>
            <div className="text-center space-y-2">
              <p className="text-muted-foreground">
                No saved info found. Fetch pharmaceutical details for
              </p>
              <p className="font-semibold text-foreground text-lg">{itemName}</p>
            </div>
            <Button 
              onClick={fetchFromAI} 
              className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all" 
              data-testid="ai-fetch-btn"
            >
              <Sparkles className="w-4 h-4" /> 
              Fetch Info with Claude Opus 4.7
            </Button>
            <p className="text-[10px] text-gray-400 uppercase tracking-widest">
              Powered by Anthropic's most capable model
            </p>
          </div>
        )}

        {/* Loading from AI - NutriTruth Style */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <style>{loadingStyles}</style>
            {/* Emerald gradient spinner like NutriTruth */}
            <div className="relative">
              <div className="w-16 h-16 rounded-full border-4 border-emerald-100 border-t-emerald-500 spinner-smooth"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
            {/* Pulsing text like NutriTruth */}
            <p className="analyzing-text text-gray-500 font-medium uppercase text-xs tracking-widest text-center">
              Claude Opus 4.7 is analyzing pharmaceutical data...
            </p>
            <p className="text-[10px] text-gray-400 text-center max-w-xs">
              Fetching detailed medicine information in English, Arabic & French
            </p>
          </div>
        )}

        {/* Error - Enhanced styling */}
        {error && (
          <div className="py-8 text-center space-y-4">
            <div className="w-14 h-14 mx-auto rounded-full bg-red-50 flex items-center justify-center">
              <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="space-y-2">
              <p className="font-semibold text-gray-900">Analysis Failed</p>
              <p className="text-red-500 text-sm max-w-md mx-auto">{error}</p>
            </div>
            <Button 
              variant="outline" 
              onClick={fetchFromAI}
              className="gap-2 border-emerald-200 text-emerald-600 hover:bg-emerald-50"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </Button>
          </div>
        )}

        {/* Info display (from saved or from AI) */}
        {info && (
          <div className="space-y-4">
            {/* Source badge + Refresh button */}
            <div className="flex items-center justify-between">
              <span className={`text-xs px-3 py-1 rounded-full font-medium ${fromSaved ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-50 text-emerald-600 border border-emerald-200'}`}>
                {fromSaved ? '✓ Loaded from saved data' : '✨ Fetched from Claude Opus 4.7'}
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                className="gap-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" 
                onClick={fetchFromAI} 
                disabled={loading} 
                data-testid="ai-refresh-btn"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Refresh with AI
              </Button>
            </div>

            {/* Language tabs */}
            <div className="flex gap-1 border-b border-border pb-1" data-testid="ai-lang-tabs">
              {LANGS.map(lang => (
                <button
                  key={lang.key}
                  onClick={() => setActiveLang(lang.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t text-sm font-medium transition-colors ${
                    activeLang === lang.key
                      ? 'bg-primary/10 text-primary border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                  data-testid={`ai-lang-${lang.key}`}
                >
                  <Globe className="w-3.5 h-3.5" />
                  {lang.label}
                </button>
              ))}
            </div>

            {/* Info fields */}
            <div className="space-y-3" dir={activeLang === 'ar' ? 'rtl' : 'ltr'} data-testid="ai-info-content">
              {FIELDS.map(field => {
                const value = langData[field.key];
                if (!value) return null;
                return (
                  <div key={field.key} className="border border-border/50 rounded-lg p-3">
                    <div className="text-xs font-semibold text-primary/70 uppercase tracking-wide mb-1">
                      {field.label[activeLang] || field.label.en}
                    </div>
                    <div className="text-sm leading-relaxed">{value}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {info && (currentItemId || itemId) && (
            <Button 
              onClick={handleSave} 
              disabled={saving || fromSaved} 
              className={`gap-2 ${fromSaved ? 'bg-gray-100 text-gray-500' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
              data-testid="ai-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {fromSaved ? 'Already Saved' : 'Save to Inventory'}
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AIMedicineInfoDialog;
